import { useState, useRef, useEffect } from 'react';
import { Upload, X, FileText, Loader2, Eye } from 'lucide-react';
import { fetchMediaAsBlob } from '../../services/api';
import { markFilePickerActive } from '../../context/PasscodeContext';
import { checkFileUploadPermissions } from '../../services/native';

/**
 * Simple file upload component - no base64 conversion
 * Stores the File object for later upload, or displays existing uploaded files
 */
export default function FileUpload({
  value, // { file: File } for new uploads OR { url, filename, mimeType } for existing
  onChange, // Called with { file: File } or null
  accept = 'image/*,.pdf',
  label = 'Upload File',
  maxSizeMB = 2,
  className = '',
  onView = null, // Callback to view the attachment
  compact = false // Compact inline mode for tight layouts
}) {
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // For displaying existing attachments from server (proxy URLs)
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobLoading, setBlobLoading] = useState(false);

  // Determine if this is a new file or existing attachment
  const isNewFile = value?.file instanceof File;
  const isExistingAttachment = value?.url && !isNewFile;
  const isPDF = isNewFile
    ? value.file.type === 'application/pdf'
    : value?.mimeType === 'application/pdf';

  // Load existing attachment preview
  useEffect(() => {
    let url = null;

    const loadExisting = async () => {
      if (isExistingAttachment && value.url.startsWith('/api/media')) {
        setBlobLoading(true);
        try {
          url = await fetchMediaAsBlob(value.url);
          setBlobUrl(url);
        } catch (err) {
          console.error('Failed to load attachment:', err);
        } finally {
          setBlobLoading(false);
        }
      }
    };

    loadExisting();

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [isExistingAttachment, value?.url]);

  // Create preview URL for new files
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (isNewFile && !isPDF) {
      const url = URL.createObjectURL(value.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [isNewFile, isPDF, value?.file]);

  const handleFileSelect = (file) => {
    setError(null);

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';

    if (!isImage && !isPdf) {
      setError('Only images and PDF files are allowed');
      return;
    }

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File must be less than ${maxSizeMB}MB`);
      return;
    }

    // Pass the file object to parent
    onChange({ file, filename: file.name, mimeType: file.type });
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange(null);
    setError(null);
  };

  const handleView = (e) => {
    e.stopPropagation();
    if (!onView) return;

    let src = null;
    let mimeType = null;
    let filename = null;

    if (isNewFile) {
      src = previewUrl || URL.createObjectURL(value.file);
      mimeType = value.file.type;
      filename = value.file.name;
    } else if (isExistingAttachment) {
      src = blobUrl;
      mimeType = value.mimeType;
      filename = value.filename;
    }

    if (src) {
      onView(src, mimeType, filename);
    }
  };

  const openFilePicker = async () => {
    // Check permissions on native before opening picker
    const hasPermission = await checkFileUploadPermissions();
    if (!hasPermission) return;

    // Mark file picker as active to skip passcode lock when returning from gallery/files
    markFilePickerActive();
    fileInputRef.current?.click();
  };

  // Get display filename
  const displayFilename = isNewFile ? value.file.name : value?.filename || 'attachment';

  // Has a value to display
  const hasValue = isNewFile || isExistingAttachment;

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />

      {!hasValue ? (
        // Upload area
        compact ? (
          <button
            type="button"
            onClick={openFilePicker}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-dashed border-surface-300 rounded-xl text-surface-500 hover:border-primary-400 hover:text-primary-600 transition-all"
          >
            <Upload className="w-4 h-4" />
            <span className="text-sm">Add file</span>
          </button>
        ) : (
        <div
          onClick={openFilePicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all
            ${dragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-surface-300 hover:border-primary-400 hover:bg-surface-50'
            }
          `}
        >
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="w-8 h-8 text-surface-400" />
            <div>
              <p className="text-sm font-medium text-surface-700">{label}</p>
              <p className="text-xs text-surface-500 mt-0.5">
                Images or PDF (max {maxSizeMB}MB)
              </p>
            </div>
          </div>
        </div>
        )
      ) : compact ? (
        // Compact preview - small inline chip
        <div className="flex items-center gap-2 px-3 py-2 border border-surface-200 rounded-xl bg-surface-50 h-[46px]">
          {blobLoading ? (
            <Loader2 className="w-4 h-4 text-surface-400 animate-spin flex-shrink-0" />
          ) : isPDF ? (
            <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-red-500" />
            </div>
          ) : (previewUrl || blobUrl) ? (
            <img src={previewUrl || blobUrl} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 bg-surface-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-surface-400" />
            </div>
          )}
          <span className="text-sm text-surface-700 truncate flex-1 min-w-0">{displayFilename}</span>
          <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
            {onView && (
              <button type="button" onClick={handleView} className="p-1.5 hover:bg-surface-200 rounded-full transition-colors">
                <Eye className="w-4 h-4 text-surface-500" />
              </button>
            )}
            <button type="button" onClick={handleRemove} className="p-1.5 hover:bg-red-100 rounded-full transition-colors">
              <X className="w-4 h-4 text-surface-500 hover:text-red-500" />
            </button>
          </div>
        </div>
      ) : (
        // Full preview area
        <div className="relative border border-surface-200 rounded-xl overflow-hidden bg-surface-50">
          {blobLoading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : isPDF ? (
            // PDF Preview
            <div className="flex items-center gap-3 p-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-900 truncate">
                  {displayFilename}
                </p>
                <p className="text-xs text-surface-500">PDF Document</p>
              </div>
              <div className="flex items-center gap-1">
                {onView && (
                  <button
                    type="button"
                    onClick={handleView}
                    className="p-1.5 bg-primary-100 hover:bg-primary-200 rounded-full transition-colors"
                    title="View document"
                  >
                    <Eye className="w-4 h-4 text-primary-600" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-1.5 bg-surface-100 hover:bg-red-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-surface-600 hover:text-red-600" />
                </button>
              </div>
            </div>
          ) : (
            // Image Preview
            <div className="relative">
              {(previewUrl || blobUrl) ? (
                <img
                  src={previewUrl || blobUrl}
                  alt="Preview"
                  className="w-full h-32 object-contain bg-white"
                />
              ) : (
                <div className="w-full h-32 flex items-center justify-center bg-surface-100">
                  <p className="text-sm text-surface-500">Preview not available</p>
                </div>
              )}
              <div className="absolute top-2 right-2 flex items-center gap-1">
                {onView && (previewUrl || blobUrl) && (
                  <button
                    type="button"
                    onClick={handleView}
                    className="p-1.5 bg-black/50 hover:bg-primary-500 rounded-full transition-colors"
                    title="View image"
                  >
                    <Eye className="w-4 h-4 text-white" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-1.5 bg-black/50 hover:bg-red-500 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                <p className="text-xs text-white truncate">{displayFilename}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 mt-2">{error}</p>
      )}
    </div>
  );
}
